/**
 * MagicLinkCallback — landing page for magic-link email clicks.
 *
 * Route: /auth/magic?token=<one-time-token>
 *
 * Flow mirrors AuthCallback.js (the Google one) so both entry points feel
 * identical to a viewer: same "SIGNAL LOCK / TUNING IN…" ident, same
 * redirect-to-cabinet-on-success, same crt:auth-changed dispatch so
 * useAuth re-hydrates without a hard reload.
 *
 * Differences vs the Google callback:
 *   • Reads `?token=` from the QUERY string (not the URL fragment) —
 *     magic links live in a real query so a mail client that strips
 *     fragments (some Outlook web builds) still delivers the token.
 *   • Success message reads "SIGNED IN" instead of "TUNING IN" because
 *     the exchange completes on the SAME response that set the cookie —
 *     no extra /me round trip.
 *
 * Ref-guarded (StrictMode double-mount) so the one-shot token isn't
 * consumed twice and lost to the "already used" error.
 */
import { useEffect, useRef, useState } from "react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function MagicLinkCallback() {
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Two guards in tandem:
    //   • useRef survives within a single mount (fast path).
    //   • sessionStorage survives across StrictMode's dev-mode
    //     mount → unmount → remount cycle, which destroys refs and
    //     would otherwise cause a second /magic-verify call on the
    //     already-consumed token (flashing "already used" on top of
    //     a successful sign-in). Keyed by token so a genuine second
    //     attempt with a different token still works.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) {
      setError("Missing sign-in token — please request a new link.");
      setTimeout(() => { window.location.replace("/"); }, 2000);
      return;
    }
    const guardKey = `crt_magic_verified_${token}`;
    let alreadyVerified = false;
    try { alreadyVerified = !!sessionStorage.getItem(guardKey); }
    catch (e) { /* private mode / storage disabled — accept the risk */ }
    if (alreadyVerified) {
      // StrictMode remount after a successful first pass. Just bounce
      // to the cabinet — the cookie is already set.
      window.history.replaceState({}, "", "/");
      window.location.replace("/");
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API}/auth/magic-verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include", // ESSENTIAL — accept the session cookie
          body: JSON.stringify({ token }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `HTTP ${res.status}`);
        }
        try { sessionStorage.setItem(guardKey, "1"); } catch (e) { /* noop */ }
        // Clear the token from history so a reload doesn't try to
        // re-verify a now-consumed token and land on the error state.
        window.history.replaceState({}, "", "/");
        window.dispatchEvent(new CustomEvent("crt:auth-changed"));
        window.location.replace("/");
      } catch (e) {
        setError(`Sign-in failed: ${e.message || e}`);
        setTimeout(() => { window.location.replace("/"); }, 2800);
      }
    })();
  }, []);

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center bg-black text-[var(--phosphor)]"
      data-testid="magic-callback"
      style={{ fontFamily: "VT323, monospace" }}
    >
      <div className="border-2 border-[var(--phosphor)] px-8 py-6 bg-black/85 phosphor-glow text-center shadow-[0_0_30px_color-mix(in_srgb,var(--phosphor)_35%,transparent)]">
        <div className="text-xs tracking-[0.4em] opacity-70 mb-2">┌─ SIGNAL LOCK ─┐</div>
        <div className="text-xl tracking-[0.25em]">
          {error ? "TRANSMISSION FAILED" : "SIGNING IN…"}
        </div>
        {error && (
          <div className="text-[11px] opacity-70 mt-2 max-w-[320px]" data-testid="magic-callback-error">
            {error}
          </div>
        )}
        <div className="text-[10px] opacity-50 mt-3 tracking-[0.18em]">redirecting to cabinet…</div>
      </div>
    </div>
  );
}
